import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from '@headlessui/react';
import { formatPrice } from 'shared/utils/currency';
import {
    MapPinIcon,
    HomeIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon,
    BuildingOfficeIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { fetchEstateByIdThunk } from 'shared/stores/store';
import { addFavoriteEstate, removeFavoriteEstate } from 'shared/api/api';
import { useTranslation } from "react-i18next";
import DocumentSection from "./Estates/EstateDocuments";
import EstateActions from "./Estates/EstateActions";
import KeyDetails from "./Estates/KeyDetails";
import NewestEstates from "./Estates/EstateGrid";

export default function EstateDetailPage() {
    const { id } = useParams();
    const { t, i18n } = useTranslation('common');
    const dispatch = useDispatch();
    const estate = useSelector((state) => state.estates.current);
    const loading = useSelector((state) => state.estates.loading);
    const error = useSelector((state) => state.estates.error);
    const favorites = useSelector((state) => state.auth.favorites) || [];
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const [isSlideshow, setIsSlideshow] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isFavoriting, setIsFavoriting] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);

    // Touch/swipe state
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const slideshowRef = useRef(null);

    const isFavorited = favorites.some(fav => fav.estate_id === estate?.id);

    const currentLocale = i18n.language;

    // Function to get the correct title based on locale
    const getLocalizedTitle = () => {
        // If the estate has _locales and a translation exists for current locale
        if (estate._locales && estate._locales[currentLocale]) {
            return estate._locales[currentLocale].title;
        }
        // Fallback to default title
        return estate.title;
    };

    const getLocalizedDescription = () => {
        // If the estate has _locales and a translation exists for current locale
        if (estate._locales && estate._locales[currentLocale]) {
            return estate._locales[currentLocale].description;
        }
        // Fallback to default title
        return estate.description;
    };

    // Function to get localized region content
    const getLocalizedRegionContent = (fieldName) => {
        const region = estate?.region;
        if (!region) return '';

        // Check if region has localized content for the current locale
        if (region._locales && region._locales[currentLocale] && region._locales[currentLocale][fieldName]) {
            return region._locales[currentLocale][fieldName];
        }

        // Fallback to default content
        return region[fieldName] || '';
    };

    // Helper functions for specific region content fields
    const getLocalizedRegionDescription = () => getLocalizedRegionContent('description');
    const getLocalizedRegionNotableLandmarks = () => getLocalizedRegionContent('notable_landmarks');
    const getLocalizedRegionAnnualEvents = () => getLocalizedRegionContent('annual_events');

    useEffect(() => {
        if (id) {
            dispatch(fetchEstateByIdThunk(id));
        }
    }, [dispatch, id]);

    const handleFavoriteClick = async (e) => {
        e.preventDefault();
        if (isFavoriting || !isAuthenticated) return;

        try {
            setIsFavoriting(true);
            if (isFavorited) {
                const favoriteId = favorites.find(fav => fav.estate_id === estate.id).id;
                const { data } = await removeFavoriteEstate(favoriteId);
                dispatch({ type: 'auth/SET_FAVORITES', payload: data });
            } else {
                const { data } = await addFavoriteEstate(estate.id);
                dispatch({ type: 'auth/SET_FAVORITES', payload: data });
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        } finally {
            setIsFavoriting(false);
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl text-gray-600">{t('estate.loading', 'Loading...')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl text-red-600">{t('estate.errors.general', { message: error })}</div>
            </div>
        );
    }

    if (!estate) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl text-gray-600">{t('estate.errors.notFound', 'No property found')}</div>
            </div>
        );
    }

    const getEstateImages = () => {
        const images = [];
        const watermarkedImages = [];

        // Loop through keys "one" to "fifteen" dynamically
        for (let i = 1; i <= 15; i++) {
            const key = i === 1 ? 'one' : i === 2 ? 'two' : i === 3 ? 'three' : i === 4 ? 'four' :
                i === 5 ? 'five' : i === 6 ? 'six' : i === 7 ? 'seven' : i === 8 ? 'eight' :
                    i === 9 ? 'nine' : i === 10 ? 'ten' : i === 11 ? 'eleven' : i === 12 ? 'twelve' :
                        i === 13 ? 'thirteen' : i === 14 ? 'fourteen' : 'fifteen';

            // Check if the image exists and if there's a watermarked version
            if (estate.images?.[key]) {
                // Original image (for fallback)
                images.push(estate.images[key]);

                // Watermarked image if available
                if (estate.images?.watermarked?.[key]) {
                    watermarkedImages.push(estate.images.watermarked[key]);
                } else {
                    // Fallback to original if watermarked not available
                    watermarkedImages.push(estate.images[key]);
                }
            }
        }

        // Filter out any null or undefined values and return the array
        return {
            original: images.filter(Boolean),
            watermarked: watermarkedImages.filter(Boolean)
        };
    };

    const { original, watermarked } = getEstateImages();
    const images = watermarked; // Use watermarked images by default
    const handleImageClick = (index) => {
        setCurrentImageIndex(index);
        setIsSlideshow(true);
    };

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const previousImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    // Touch handlers for swipe functionality
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextImage();
        }
        if (isRightSwipe) {
            previousImage();
        }

        // Reset values
        setTouchStart(null);
        setTouchEnd(null);
    };

    const getFeatures = () => {
        const features = {
            private: [],
            community: []
        };

        Object.entries(estate).forEach(([key, value]) => {
            if (value === 1 || value === true) {  // Check for both 1 and true
                if (key.startsWith('private_')) {
                    const featureKey = key.replace('private_', '');
                    features.private.push(featureKey);
                } else if (key.startsWith('community_')) {
                    const featureKey = key.replace('community_', '');
                    features.community.push(featureKey);
                }
            }
        });

        return features;
    };

    const FeatureSection = () => {
        const features = getFeatures();

        return (
            <div className="space-y-8">
                {/* Private Features */}
                {features.private.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-lg font-semibold mb-4">{t('estate.details.features.private_features', 'Private Features')}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4">
                            {features.private.map((feature) => (
                                <div key={feature} className="flex items-center space-x-2">
                                    <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                                    <span
                                        className="text-gray-700">{t(`estate.details.features.private.${feature}`, feature)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Community Features */}
                {features.community.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                        <h3 className="text-lg font-semibold mb-4">{t('estate.details.features.community_features', 'Community Features')}</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4">
                            {features.community.map((feature) => (
                                <div key={feature} className="flex items-center space-x-2">
                                    <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                                    <span className="text-gray-700">{t(`estate.details.features.community.${feature}`, feature)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!estate) return null;

    return (
        <main className="bg-white pt-6">

            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb">
                <ol role="list"
                    className="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                    <li>
                        <div className="flex items-center">
                            <a href="#"
                                className="mr-2 text-sm font-medium text-gray-900">{t('estate.breadcrumb.spain')}</a>
                            <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" aria-hidden="true"
                                className="h-5 w-4 text-gray-300">
                                <path d="M5.697 4.34L8.98 16.532h1.327L7.025 4.341H5.697z"></path>
                            </svg>
                        </div>
                    </li>
                    <li>
                        <div className="flex items-center">
                            <a href="#" className="mr-2 text-sm font-medium text-gray-900">{estate.region.name}</a>
                            <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" aria-hidden="true"
                                className="h-5 w-4 text-gray-300 hidden md:block">
                                <path d="M5.697 4.34L8.98 16.532h1.327L7.025 4.341H5.697z"></path>
                            </svg>
                        </div>
                    </li>

                    <li className="text-sm hidden md:block">
                        <a href="#" aria-current="page"
                            className="font-medium text-gray-500 hover:text-gray-600">{getLocalizedTitle()}</a>
                    </li>
                </ol>
            </nav>
            {/* Slideshow Modal */}
            <Dialog
                open={isSlideshow}
                onClose={() => setIsSlideshow(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/95" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center">
                    <Dialog.Panel className="w-screen h-screen flex flex-col justify-center relative">
                        {/* Close button */}
                        <button
                            onClick={() => setIsSlideshow(false)}
                            className="fixed top-4 right-4 text-white hover:text-gray-300 z-50 p-3 bg-black/80 rounded-full shadow-lg"
                        >
                            <XMarkIcon className="h-8 w-8" />
                        </button>

                        {/* Main image container */}
                        <div
                            ref={slideshowRef}
                            className="relative h-screen w-full flex items-center justify-center px-2 md:px-12"
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <img
                                src={images[currentImageIndex]}
                                alt={`Slide ${currentImageIndex + 1}`}
                                className="max-h-[85vh] w-full md:w-auto object-contain"
                            />

                            {/* Navigation buttons - Hidden on mobile, shown on swipe */}
                            <button
                                onClick={previousImage}
                                className="hidden md:block absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 p-2 md:p-4 rounded-full text-white hover:bg-black/70 transition-colors"
                            >
                                <ChevronLeftIcon className="h-6 w-6 md:h-8 md:w-8" />
                            </button>
                            <button
                                onClick={nextImage}
                                className="hidden md:block absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 p-2 md:p-4 rounded-full text-white hover:bg-black/70 transition-colors"
                            >
                                <ChevronRightIcon className="h-6 w-6 md:h-8 md:w-8" />
                            </button>

                            {/* Mobile swipe indicators */}
                            <div className="absolute inset-y-0 left-0 w-1/3 md:hidden flex items-center" onClick={previousImage}>
                                <div className="ml-4 bg-black/50 rounded-full p-3">
                                    <ChevronLeftIcon className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <div className="absolute inset-y-0 right-0 w-1/3 md:hidden flex items-center justify-end" onClick={nextImage}>
                                <div className="mr-4 bg-black/50 rounded-full p-3">
                                    <ChevronRightIcon className="h-4 w-4 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Image counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                            {currentImageIndex + 1} / {images.length}
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* Image Grid */}
            <div className="mx-auto mt-6 max-w-2xl sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-3 lg:gap-x-8 lg:px-8">
                <div className="aspect-h-4 aspect-w-3 hidden overflow-hidden rounded-lg lg:block">
                    <img
                        src={images[0]}
                        className="h-full w-full object-cover object-center cursor-pointer transition-transform hover:scale-105"
                        onClick={() => handleImageClick(0)}
                        alt="Property main view"
                    />
                </div>
                <div className="hidden lg:grid lg:grid-cols-1 lg:gap-y-8">
                    {images.slice(1, 3).map((image, idx) => (
                        <div key={idx} className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
                            <img
                                src={image}
                                className="h-full w-full object-cover object-center cursor-pointer transition-transform hover:scale-105"
                                onClick={() => handleImageClick(idx + 1)}
                                alt={`Property view ${idx + 2}`}
                            />
                        </div>
                    ))}
                </div>
                <div className="aspect-h-5 aspect-w-4 lg:aspect-h-4 lg:aspect-w-3 sm:overflow-hidden sm:rounded-lg">
                    <img
                        src={images[3]}
                        className="h-full w-full object-cover object-center cursor-pointer transition-transform hover:scale-105"
                        onClick={() => handleImageClick(3)}
                        alt="Property additional view"
                    />
                </div>
            </div>
            {/* Main Content */}
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-8 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Main Details */}
                <div className="lg:col-span-2 space-y-8">
                    <div>
                        <h1 className="text-xl md:text-4xl font-bold text-gray-900 mb-4">{getLocalizedTitle()}</h1>
                        <div className="text-xs md:text-base flex items-center space-x-4 text-gray-600">
                            <MapPinIcon className="h-5 w-5" />
                            <span>{estate.address_city}</span>
                        </div>
                    </div>

                    {/* Key Details */}
                    <KeyDetails estate={estate} t={t} />

                    {/* Features */}

                    <FeatureSection />

                    {/* Description */}
                    <div className="prose prose-lg max-w-none">
                        <h2 className="text-lg md:text-2xl font-semibold mb-4"> {t('estate.details.description.title')}</h2>
                        <p className="text-gray-600 whitespace-pre-line text-sm md:text-base">{getLocalizedDescription()}</p>
                    </div>

                    {/* Features and Documents */}

                    {estate.video && (
                        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-8">
                            <div className="aspect-w-16 aspect-h-9">
                                <iframe
                                    src={estate.video.includes('youtube.com/watch?v=')
                                        ? estate.video.replace('watch?v=', 'embed/')
                                        : estate.video}
                                    className="w-full h-96 rounded-xl"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    )}


                    {/* Distances */}
                    <div className="bg-gray-50 rounded-xl p-6">
                        <h2 className="text-2xl font-semibold mb-6">{t('estate.details.distances.title')}</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-3">
                                <div>
                                    <p className="text-sm text-gray-500">{t('estate.details.distances.beach')}</p>
                                    <p className="font-medium">{estate.distance_to_beach} {t('estate.details.distances.unit')}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div>
                                    <p className="text-sm text-gray-500">{t('estate.details.distances.airport')}</p>
                                    <p className="font-medium">{estate.distance_to_airport} {t('estate.details.distances.unit')}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div>
                                    <p className="text-sm text-gray-500">{t('estate.details.distances.golf')}</p>
                                    <p className="font-medium">{estate.distance_to_golf} {t('estate.details.distances.unit')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DocumentSection
                        documents={estate.documents}
                        isAuthenticated={isAuthenticated}
                        t={t}
                    />
                </div>

                {/* Right Column - Price and Contact */}
                <div className="lg:col-span-1">
                    <div className="sticky top-24">
                        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                            <div className="mb-6">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm text-gray-500 font-semibold">{t('estate.details.price.title')}</p>
                                        <p className="text-3xl font-bold text-gray-900 text-right">
                                            {formatPrice(estate.price)}
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {formatPrice(Math.round(estate.price / estate.living_area))} / m²
                                        </p>
                                    </div>
                                    {isAuthenticated && (
                                        <button
                                            onClick={handleFavoriteClick}
                                            disabled={isFavoriting}
                                            className="p-2 pt-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                        >
                                            {isFavorited ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                    fill="currentColor" className="w-10 h-10 text-red-500">
                                                    <path
                                                        d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                    strokeWidth={1.5} stroke="currentColor" className="w-10 h-10">
                                                    <path strokeLinecap="round" strokeLinejoin="round"
                                                        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <EstateActions
                                estate={estate}
                                t={t}
                                isAuthenticated={isAuthenticated}
                            />


                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="font-medium mb-4">{t('estate.details.propertyInfo.title')}</h3>
                                <dl className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">{t('estate.details.propertyInfo.reference')}</dt>
                                        <dd className="font-medium">{estate.reference_number}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">{t('estate.details.propertyInfo.status')}</dt>
                                        <dd className="font-medium capitalize">{estate.status}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">{t('estate.details.propertyInfo.yearBuilt')}</dt>
                                        <dd className="font-medium">{estate.year_built}</dd>
                                    </div>
                                    {estate.year_renovated && (
                                        <div className="flex justify-between">
                                            <dt className="text-gray-500">{t('estate.details.propertyInfo.renovated')}</dt>
                                            <dd className="font-medium">{estate.year_renovated}</dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <dt className="text-gray-500">{t('estate.details.propertyInfo.energyRating')}</dt>
                                        <dd className="font-medium">{estate.energy_rating}</dd>
                                    </div>
                                </dl>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 mt-24">
                <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-32 lg:max-w-7xl lg:px-8 bg-gray-50">

                    <section aria-labelledby="details-heading">
                        <div className="flex flex-col items-center text-center">
                            <h2 id="details-heading"
                                className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{estate.region.name}</h2>
                            <p className="mt-3 max-w-3xl text-lg text-gray-600">{getLocalizedRegionDescription()}</p>
                        </div>

                        <div className="mt-16 grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-8">
                            <div>
                                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                                    <img
                                        src={estate.region.image1}
                                        className="h-full w-full object-cover object-center" />
                                </div>
                            </div>
                            <div>
                                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                                    <img
                                        src={estate.region.image2}
                                        className="h-full w-full object-cover object-center" />
                                </div>

                            </div>
                        </div>
                    </section>

                    <div>

                        <div className="mx-auto max-w-2xl lg:max-w-none mt-24">
                            <div className="text-center">
                                <h2 className="text-balance text-xl font-semibold tracking-tight text-gray-900 sm:text-4xl">{t('region.about.title')}</h2>
                                <p className="mt-4 text-lg leading-8 text-gray-600">{t('region.about.subtitle', { region: estate.region.name })}</p>
                            </div>
                            <dl className="mt-16 grid grid-cols-1 gap-0.5 overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-4">
                                <div className="flex flex-col bg-gray-400/5 p-8">
                                    <dt className="text-sm font-semibold leading-6 text-gray-600">
                                        {t('region.statistics.area')}
                                    </dt>
                                    <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">{estate.region.area_km2} km²</dd>
                                </div>
                                <div className="flex flex-col bg-gray-400/5 p-8">
                                    <dt className="text-sm font-semibold leading-6 text-gray-600">
                                        {t('region.statistics.population')}
                                    </dt>
                                    <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">{estate.region.population?.toLocaleString()}</dd>
                                </div>
                                <div className="flex flex-col bg-gray-400/5 p-8">
                                    <dt className="text-sm font-semibold leading-6 text-gray-600">
                                        {t('region.statistics.avgTemp')}
                                    </dt>
                                    <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">{estate.region.avg_temperature} °C</dd>
                                </div>
                                <div className="flex flex-col bg-gray-400/5 p-8">
                                    <dt className="text-sm font-semibold leading-6 text-gray-600">
                                        {t('region.statistics.currency')}
                                    </dt>
                                    <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900">
                                        {t('region.statistics.currencyValue')}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        <div className="mt-16 grid grid-cols-1 gap-y-16 lg:grid-cols-3 lg:gap-x-8">
                            <div>
                                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                                    <img
                                        src={estate.region.image1}
                                        className="h-full w-full object-cover object-center" />
                                </div>
                            </div>
                            <div>
                                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                                    <img
                                        src={estate.region.image3}
                                        className="h-full w-full object-cover object-center" />
                                </div>

                            </div>
                            <div>
                                <div className="aspect-h-2 aspect-w-3 w-full overflow-hidden rounded-lg">
                                    <img
                                        src={estate.region.image4}
                                        className="h-full w-full object-cover object-center" />
                                </div>

                            </div>
                        </div>
                        <dl className="mt-16 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 sm:gap-y-16 lg:grid-cols-3 lg:gap-x-8">
                            {/* Landmarks */}
                            <div className="border-gray-200">
                                <dt className="font-medium text-gray-900">{t('region.features.landmarks')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">
                                    {getLocalizedRegionNotableLandmarks()}
                                </dd>
                            </div>

                            {/* Annual Events */}
                            <div className="border-gray-200">
                                <dt className="font-medium text-gray-900">{t('region.features.events')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">{getLocalizedRegionAnnualEvents()}</dd>
                            </div>

                            <div className="border-gray-200">
                                <dt className="font-medium text-gray-900">{t('region.features.climate.title')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">
                                    {t('region.features.climate.description', {
                                        region: estate.region.name,
                                        avgTemp: estate.region.avg_temperature
                                    })}
                                </dd>
                            </div>

                            {/* Transport */}
                            <div className="border-t border-gray-200 pt-4">
                                <dt className="font-medium text-gray-900">{t('region.features.transport')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">
                                    {t('region.features.airport')}: {estate.region.nearest_airport}
                                </dd>
                            </div>

                            {/* Amenities */}
                            <div className="border-t border-gray-200 pt-4">
                                <dt className="font-medium text-gray-900">{t('region.features.amenities.title')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">
                                    {t('region.features.amenities.restaurants')}: {estate.region.restaurants_count}
                                    <br />{t('region.features.amenities.shoppingCenters')}: {estate.region.shopping_centers_count}
                                    <br />{t('region.features.amenities.parks')}: {estate.region.parks_count}
                                    <br />{t('region.features.amenities.beaches')}: {estate.region.beaches_count}
                                </dd>
                            </div>

                            {/* Quality of Life */}
                            <div className="border-t border-gray-200 pt-4">
                                <dt className="font-medium text-gray-900">{t('region.features.quality.title')}</dt>
                                <dd className="mt-2 text-sm text-gray-500">
                                    {t('region.features.quality.safety')}: {estate.region.safety_index}/100
                                    <br />{t('region.features.quality.healthcare')}: {estate.region.healthcare_quality}
                                    <br />{t('region.features.quality.education')}: {estate.region.education_quality}
                                    <br />{t('region.features.quality.transport')}: {estate.region.public_transport_rating}
                                </dd>
                            </div>
                        </dl>

                    </div>
                </div>
            </div>
            <div className="">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-2xl font-bold">{t('region.estates.title', { region: estate.region.name })}</h2>

                    </div>

                    <NewestEstates regionId={estate.region.parent_id}></NewestEstates>
                </div>
            </div>

        </main>
    );
}